import { Box, Divider, IconButton, Typography } from '@mui/material'
import { Button } from 'global/components/Button'
import { useThemeStore } from 'global/components/ToggleTheme'
import AjudaFiltros from 'global/guides/ContentGuide/AjudaFiltros'
import localeText from 'locales/datagrid'
import { BookmarkIcon, PlusIcon, Trash2Icon, XIcon } from 'lucide-react'
import React from 'react'
import { toast } from 'sonner'
import { filtroVazio } from '../hooks/useDefaultFilterModel'
import FilterModelPro, {
    GridFilterItemPro,
    GridFilterModelPro,
} from '../utils/filterModelPro'
import { useTableApiContext } from '../utils/table-context'
import { GridColDefPro } from '../utils/types'
import FieldColuna from './FieldColuna'
import FieldLinkOperator from './FieldLinkOperator'
import FieldOperator from './FieldOperator'
import FieldValue from './FieldValue'
import FilterPreset from './FilterPreset'

export default function FilterPanelPro() {
    const {
        filterModelPro: filterModel,
        onFilterModelChangePro: onFilterModelChange,
        columns: apiColumns,
    } = useTableApiContext()

    const columns = apiColumns.filter(c => c.filterable !== false)

    const theme = useThemeStore(state => state.theme)

    function gerarLinkFiltro() {
        const link = window.location.href.split('?')[0] + '?grid-filter='
        navigator.clipboard.writeText(
            link + new FilterModelPro(filterModel).toUrlParams(),
        )
        toast.success('Link copiado para a área de transferência')
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                padding: '10px',
                width: '100%',
                columnGap: '10px',
                overflowX: 'auto',
                height: '150px',
                overflowY: 'hidden',
                border:
                    theme === 'dark'
                        ? '1px solid rgba(81, 81, 81, 1)'
                        : '1px solid rgba(224, 224, 224, 1);',
                borderBottom: 'none',
            }}
        >
            <Box // lista filtros aplicados
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    flex: 1,
                    minWidth: '23rem',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        scrollbarGutter: 'stable',
                        my: 1,
                        p: 1,
                        rowGap: '15px',
                        overflowY: 'auto',
                        maxHeight: '120px',
                        width: '100%',
                    }}
                >
                    {!filterModel.items.length ? (
                        <Typography
                            variant="body1"
                            sx={{
                                alignSelf: 'center',
                                opacity: 0.6,
                                userSelect: 'none',
                            }}
                        >
                            Nenhum filtro aplicado. Clique em "Adicionar" para
                            começar a filtrar.
                        </Typography>
                    ) : (
                        filterModel.items.map((item, index) => (
                            <FilterOption
                                key={index}
                                item={item}
                                onFilterModelChange={onFilterModelChange}
                                columns={columns}
                                isFirstItem={index === 0}
                            />
                        ))
                    )}
                </Box>
            </Box>
            <Box>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 0.3fr',
                        columnGap: '10px',
                        minWidth: 'fit-content',
                    }}
                >
                    <Box // botoes
                        sx={{
                            display: 'grid',
                            gridTemplateRows: '1fr 1fr',
                            rowGap: '10px',
                            minWidth: 'max-content',
                        }}
                    >
                        <Button
                            startIcon={<PlusIcon className="size-5" />}
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                onFilterModelChange(prev => ({
                                    ...prev,
                                    items: [
                                        ...prev.items,
                                        {
                                            columnField: columns[0].field,
                                            operatorValue:
                                                FilterModelPro.getDefaultOperator(
                                                    columns[0].type,
                                                ),
                                            value: '',
                                            id: FilterModelPro.gerarID(
                                                prev.items,
                                            ),
                                        },
                                    ],
                                }))
                            }}
                        >
                            Adicionar
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={
                                <Trash2Icon className="text-red-500 size-5" />
                            }
                            onClick={() =>
                                onFilterModelChange(prev => ({
                                    ...prev,
                                    items: filtroVazio.items,
                                }))
                            }
                            color="error"
                            size="small"
                        >
                            Limpar
                        </Button>
                    </Box>
                    <Box // icones
                        sx={{
                            display: 'grid',
                            gridTemplateRows: '1fr 1fr',
                            columnGap: '10px',
                            justifyContent: 'center',
                            minWidth: 'fit-content',
                            alignItems: 'center',
                        }}
                    >
                        <div
                            style={{
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                overflow: 'hidden',
                            }}
                            title="Clique para ver a ajuda"
                        >
                            <AjudaFiltros />
                        </div>
                        <IconButton
                            style={{
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                            title="Clique para copiar o link do filtro"
                            onClick={gerarLinkFiltro}
                        >
                            <BookmarkIcon />
                        </IconButton>
                    </Box>
                </Box>
                <Box // presets
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        minWidth: 'fit-content',
                        mt: 1,
                    }}
                >
                    <FilterPreset
                        onFilterModelChange={onFilterModelChange}
                        filterModel={filterModel}
                        columns={columns}
                    />
                </Box>
            </Box>
        </Box>
    )
}

const FilterOption = ({
    item,
    onFilterModelChange,
    columns,
    isFirstItem,
}: {
    item: GridFilterItemPro
    onFilterModelChange: React.Dispatch<
        React.SetStateAction<GridFilterModelPro>
    >
    columns: GridColDefPro[]
    isFirstItem: boolean
}) => {
    const column = columns.find(column => column.field === item.columnField)

    const MemoizedLinkOperator = React.useMemo(
        () =>
            isFirstItem ? (
                <Box
                    sx={{
                        minWidth: '4.5rem',
                        maxWidth: '4.5rem',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Divider sx={{ width: '100%' }} />
                </Box>
            ) : (
                <FieldLinkOperator
                    item={item}
                    onFilterModelChange={onFilterModelChange}
                    sx={{ minWidth: '4.5rem', maxWidth: '4.5rem' }}
                />
            ),
        [item.linkOperator],
    )

    const MemoizedFieldOperator = React.useMemo(
        () =>
            column ? (
                <FieldOperator
                    item={item}
                    column={column}
                    onFilterModelChange={onFilterModelChange}
                    sx={{ minWidth: '10rem', maxWidth: '12rem' }}
                />
            ) : null,
        [item.operatorValue, item.columnField],
    )

    const MemoizedFieldColuna = React.useMemo(
        () => (
            <FieldColuna
                item={item}
                columns={columns}
                onFilterModelChange={onFilterModelChange}
                sx={{ minWidth: '8rem', maxWidth: '15rem' }}
            />
        ),
        [item.columnField],
    )

    if (!column) return null

    return (
        <Box
            sx={{
                display: 'flex',
                width: '100%',
                columnGap: '5px',
                height: 'fit-content',
            }}
        >
            <IconButton
                onClick={() =>
                    onFilterModelChange(prev => ({
                        ...prev,
                        items: prev.items.filter(({ id }) => id !== item.id),
                    }))
                }
                title={localeText.filterPanelDeleteIconLabel}
                sx={{ minWidth: '2.5rem' }}
            >
                <XIcon className="text-red-500" />
            </IconButton>
            {MemoizedLinkOperator}
            {MemoizedFieldColuna}
            {MemoizedFieldOperator}
            <FieldValue
                item={item}
                column={column}
                onFilterModelChange={onFilterModelChange}
                sx={{ minWidth: '8rem', maxWidth: '15rem' }}
            />
        </Box>
    )
}
